import { DialogTitle } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import { useRevalidator, useSearchParams } from "react-router";
import { PrimaryButton } from "~/components/Button";
import { Text } from "~/components/Text";
import { PlanDialog } from "~/components/plans/components/PlanDialog";
import GreenFlowerImage from "~/images/green-flower.png";
import { PhoneAuthentication, type PhoneAuthenticationDraft } from "./PhoneAuthentication";

export type TicketStatus = {
  state: "login" | "confirmed" | "unavailable" | "error";
};

type Props = {
  eventId: string;
  planTitle: string;
  openAppLink: string;
  status: TicketStatus | null;
};

export function TicketStatusDialog({ eventId, planTitle, openAppLink, status }: Props) {
  const [, setSearch] = useSearchParams();
  const { revalidate } = useRevalidator();
  const [signingIn, setSigningIn] = useState(false);
  const [refreshingFrom, setRefreshingFrom] = useState<TicketStatus | null>();
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [phone, setPhone] = useState<PhoneAuthenticationDraft>({
    phone: "", phoneInput: "", phoneCountry: "", verificationSentAt: 0,
  });
  const active = useRef(true);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const close = () => {
    active.current = false;
    setSearch(previous => {
      const next = new URLSearchParams(previous);
      next.delete("status");
      next.delete("step");
      return next;
    }, { replace: true, preventScrollReset: true });
  };

  const refresh = async () => {
    if (!active.current) return;
    setRefreshFailed(false);
    setSigningIn(false);
    // The promise can settle before React commits new loader data. Never reuse
    // the previous account's confirmation while waiting for that new result.
    setRefreshingFrom(status);
    try {
      await revalidate();
    } catch {
      if (active.current) setRefreshFailed(true);
    }
  };

  const waitingForSession = refreshingFrom !== undefined && refreshingFrom === status && !refreshFailed;
  const showLogin = !refreshFailed && !waitingForSession && (signingIn || status?.state === "login");
  const checking = !refreshFailed && (waitingForSession || !status);
  const confirmed = !refreshFailed && status?.state === "confirmed";
  const failed = refreshFailed || status?.state === "error";

  return (
    <PlanDialog onClose={close} fullScreenOnMobile>
      {showLogin ? (
        <PhoneAuthentication
          eventId={eventId}
          draft={phone}
          update={patch => setPhone(previous => ({ ...previous, ...patch }))}
          canContinue={() => active.current}
          onBack={close}
          onComplete={refresh}
          title="View your ticket"
          compact
          description="Enter the phone number you used for your ticket. We’ll text you a code."
        />
      ) : (
        <div className="flex flex-1 flex-col pt-8 text-center">
          {checking ? (
            <>
              <Text as={DialogTitle} variant="primary">Checking your ticket</Text>
              <Text role="status" className="mt-3">This should only take a moment.</Text>
            </>
          ) : confirmed ? (
            <>
              <img src={GreenFlowerImage} alt="" className="mx-auto mb-6 h-24 w-24 object-contain" />
              <Text as={DialogTitle} variant="primary">You’re going</Text>
              <Text className="mt-3">Your ticket for {planTitle} is confirmed.</Text>
              <PrimaryButton to={openAppLink} reloadDocument className="mt-8 w-full">Open in Butter</PrimaryButton>
            </>
          ) : (
            <>
              <Text as={DialogTitle} variant="primary">
                {failed ? "We couldn’t check your ticket" : "No confirmed ticket found"}
              </Text>
              <Text className="mt-3">
                {failed
                  ? "Try again in a moment. Your ticket hasn’t been changed."
                  : "There isn’t an active, confirmed ticket for this plan on this account. If you used another number, sign in with it to check."}
              </Text>
              {failed && <PrimaryButton onClick={refresh} className="mt-8 w-full">Try again</PrimaryButton>}
              <button
                type="button"
                className="mt-5 min-h-11 font-sans text-butter-green underline underline-offset-4"
                onClick={() => {
                  setRefreshFailed(false);
                  setRefreshingFrom(undefined);
                  setPhone({ phone: "", phoneInput: "", phoneCountry: "", verificationSentAt: 0 });
                  setSigningIn(true);
                }}
              >
                Sign in with another number
              </button>
            </>
          )}
        </div>
      )}
    </PlanDialog>
  );
}
